import hotkeys from "hotkeys-js";
import { isTextEditorPointerEvent, switchCopyingToDragging } from "@/actionManager/utils";
import { ZoomToVis, type ZoomToVisTransformation } from "@/animations/ZoomToVis";
import { ACCOUNT_UPDATE_V2, BOARD_ZOOM_GET_V2, BOARD_ZOOM_SET_V2 } from "@/api/apiEndpoints";
import { canv } from "@/canv/global";
import { logException, MakeRequest, matrixTranslate } from "@/canv/utils";
import {
    getClipboardEventData,
    getViewport,
    isJespoDumpsText,
    loadFiles,
    pasteProccessor,
    setNavigationModeLS,
} from "@/canvasController/helpers";
import { eventEmitter } from "@/eventEmitter";
import { rafThrottle } from "@/helpers/rafThrottle";
import { selectionManager } from "@/tools/cursorTool";
import { isWidgetWithTextOrTextWidgetOnly } from "@/tools/cursorTool/utils";
import type { ToolName } from "@/tools/types";
import { isMobile } from "@/utils";

type OS = "Windows" | "MacOS" | "UNIX" | "Linux";
export type NavigationMode = "trackpad" | "mouse";
type GestureType = "pinch" | "pan" | undefined;
type ClipboardShortcutType = "copy" | "paste";
type HistoryShortcutType = "undo" | "redo";

interface Point {
    x: number;
    y: number;
}

interface ViewportData {
    viewportStartX: number;
    viewportStartY: number;
    viewportWidth: number;
    viewportHeight: number;
}

interface ZoomSaveRequest {
    token: string;
    boardUID: string;
    data: ViewportData & { scale: number };
}

interface ZoomResponse {
    status: string;
    data: {
        scale: number;
        viewportStartX: number;
        viewportStartY: number;
        viewportWidth: number;
        viewportHeight: number;
    };
}

type TouchWithOffset = Touch & { offsetX?: number; offsetY?: number };

export class actionManager {
    // State
    lastX = 0;
    lastY = 0;
    preX = 0;
    preY = 0;
    lastMoveEvt: PointerEvent | TouchEvent | null = null;
    lastNativeClipboardShortcut: { type: ClipboardShortcutType | ""; time: number } = {
        type: "",
        time: 0,
    };

    mode: NavigationMode = "trackpad";
    prevTouches: Touch[] = [];
    lastTouches: Touch[] = [];
    gestureType: GestureType = undefined;
    rightButtonClicked = false;
    rightButtonClickedPrevValue = false;
    clickMove = 0;
    clickMoveThreshold = 3;

    // OS-specific
    actionSymbol = "Ctrl";
    actionSymbolName = "Ctrl";
    altSymbol = "Alt";
    os: OS = "Windows";

    // Constants
    maxZoom = 5;
    minZoom = 0.01;

    constructor() {
        if (navigator.appVersion.indexOf("Win") !== -1) this.os = "Windows";
        if (navigator.appVersion.indexOf("Mac") !== -1) this.os = "MacOS";
        if (navigator.appVersion.indexOf("X11") !== -1) this.os = "UNIX";
        if (navigator.appVersion.indexOf("Linux") !== -1) this.os = "Linux";

        if (this.os === "MacOS") {
            this.actionSymbol = "&#8984;";
            this.actionSymbolName = "Cmd";
            this.altSymbol = "&#8997;";
        }
    }

    // --- Coordinate helpers ---

    getActionPoint(): Point {
        const x = this.lastX * canv.dpr;
        const y = this.lastY * canv.dpr;
        return {
            x: (x + y * canv.m[2] - canv.m[4]) / canv.m[0],
            y: (canv.m[1] * x + y - canv.m[5]) / canv.m[3],
        };
    }

    getPreActionPoint(): Point {
        const x = this.preX * canv.dpr;
        const y = this.preY * canv.dpr;
        return {
            x: (x + y * canv.m[2] - canv.m[4]) / canv.m[0],
            y: (canv.m[1] * x + y - canv.m[5]) / canv.m[3],
        };
    }

    getConstant(value: number): number {
        return value * canv.dpr;
    }

    getZoomConstant(value: number): number {
        return (value * canv.dpr) / canv.m[0];
    }

    // --- Event Listeners ---
    contextmenuHandler = (e: MouseEvent) => e.preventDefault();
    addContextmenuListener() {
        document.addEventListener("contextmenu", this.contextmenuHandler);
    }
    removeContextmenuListener() {
        document.removeEventListener("contextmenu", this.contextmenuHandler);
    }

    initEventListeners(): void {
        window.addEventListener("resize", () => {
            canv.adjustCanvas();
        });

        window.addEventListener("beforeunload", this.viewSaveSave);

        document.addEventListener("visibilitychange", () => {
            if (!document.hidden && canv.intercom.socket) {
                const { readyState } = canv.intercom.socket;
                if (readyState === 2 || readyState === 3) {
                    canv.requestReconnect("visibilitychange");
                }
            }
        });

        canv.canvas.addEventListener("dragover", (e: DragEvent) => e.preventDefault());
        canv.canvas.addEventListener("drop", this.dropElement);
        this.addContextmenuListener();
        canv.canvas.addEventListener("dblclick", this.dblClickListener, false);
        canv.canvas.addEventListener("click", this.clickListener, false);

        canv.canvas.addEventListener("pointerdown", this.pointerDownListener, false);
        document.addEventListener("pointermove", this.pointerMoveListener, false);
        document.addEventListener("pointerup", this.pointerUpListener, false);
        canv.canvas.addEventListener("pointercancel", this.pointerUpListener, false);

        canv.canvas.addEventListener("touchstart", this.touchStartListener, false);
        canv.canvas.addEventListener("touchmove", this.touchMoveListener, false);

        canv.canvas.addEventListener("wheel", this.handleTouchpadGesture, false);
        document.addEventListener("keydown", this.historyShortcutFallbackListener, true);
        document.addEventListener("keyup", this.handleKeyboardUp, false);
        document.addEventListener("copy", this.nativeClipboardShortcutListener, true);
        document.addEventListener("paste", this.nativeClipboardShortcutListener, true);

        document.addEventListener("keypress", (event: KeyboardEvent) => {
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            canv.keyboard.command = event.key;
            canv.keyboard.code = event.code;
            canv.keyboard.isSymbol = true;
            canv.actionManager.handleKeyboardDown(event);
        });

        // Убираем зуминг и скролиинг везде кроме канваса
        document.addEventListener("wheel", this.handleDocumentTouchpadGesture, { passive: false });

        this._initHotkeys();
    }

    private _initHotkeys(): void {
        hotkeys("*", (event: KeyboardEvent) => {
            let detectedAny = false;

            if (
                (event.code === "Home" && event.shiftKey) ||
                (event.code === "ArrowLeft" && event.shiftKey && event.metaKey)
            ) {
                canv.keyboard.command = "shift+home";
                detectedAny = true;
            } else if (
                (event.code === "End" && event.shiftKey) ||
                (event.code === "ArrowRight" && event.shiftKey && event.metaKey)
            ) {
                canv.keyboard.command = "shift+end";
                detectedAny = true;
            } else if (event.code === "ArrowLeft") {
                canv.keyboard.command = event.shiftKey ? "ArrowLeft+shift" : "ArrowLeft";
                detectedAny = true;
            } else if (event.code === "ArrowRight") {
                canv.keyboard.command = event.shiftKey ? "ArrowRight+shift" : "ArrowRight";
                detectedAny = true;
            } else if (event.code === "ArrowDown") {
                canv.keyboard.command = event.shiftKey ? "ArrowDown+shift" : "ArrowDown";
                detectedAny = true;
            } else if (event.code === "ArrowUp") {
                canv.keyboard.command = event.shiftKey ? "ArrowUp+shift" : "ArrowUp";
                detectedAny = true;
            } else if (event.code === "ShiftLeft" || event.code === "ShiftRight") {
                canv.keyboard.command = "Shift";
                detectedAny = true;
            } else if (
                event.code === "AltLeft" ||
                event.code === "AltRight" ||
                event.key === "Alt"
            ) {
                canv.keyboard.command = "alt";
                detectedAny = true;
            } else if (event.code === "Backspace") {
                canv.keyboard.command = "Backspace";
                detectedAny = true;
            } else if (event.code === "Delete") {
                canv.keyboard.command = "Delete";
                detectedAny = true;
            }

            if (detectedAny) {
                canv.keyboard.isSymbol = false;
                canv.actionManager.handleKeyboardDown(event);
            }
        });

        const bindHotkey = (combo: string, withForce = false) => {
            hotkeys(combo, (event: KeyboardEvent, handler: { shortcut: string }) => {
                canv.keyboard.command = handler.shortcut;
                canv.keyboard.isSymbol = false;
                canv.actionManager.handleKeyboardDown(
                    event,
                    withForce ? canv.isBoardModeViewCopy() : false,
                );
            });
        };

        hotkeys("escape", (event: KeyboardEvent) => {
            canv.keyboard.command = event.key;
            canv.keyboard.isSymbol = false;
            canv.actionManager.handleKeyboardDown(event);
        });

        bindHotkey("ctrl+a, cmd+a", true);
        bindHotkey("ctrl+z, cmd+z");
        bindHotkey("ctrl+shift+z, cmd+shift+z");
        bindHotkey("ctrl+x, cmd+x");
        bindHotkey("ctrl+d, cmd+d");
        bindHotkey("ctrl+g, cmd+g");
        bindHotkey("ctrl+shift+g, cmd+shift+g");
        bindHotkey("ctrl+], cmd+]");
        bindHotkey("ctrl+[, cmd+[");
        bindHotkey("ctrl+=, cmd+=");
        bindHotkey("ctrl+-, cmd+-");
        bindHotkey("ctrl+enter, cmd+enter");
        bindHotkey("ctrl+shift+enter, cmd+shift+enter");
        bindHotkey("ctrl+f, cmd+f");
        bindHotkey("ctrl+b, cmd+b");
        bindHotkey("shift+end, shift+cmd+arrowRight");
        bindHotkey("shift+home, shift+cmd+arrowLeft");
        bindHotkey("ctrl+0, cmd+0");
        bindHotkey("alt+1");
        bindHotkey("alt+2");
    }

    private markNativeClipboardShortcutHandled(type: ClipboardShortcutType) {
        this.lastNativeClipboardShortcut = {
            type,
            time: Date.now(),
        };
    }

    private wasShortcutRecentlyHandled(
        marker: { type: ClipboardShortcutType | ""; time: number },
        type: ClipboardShortcutType,
    ) {
        return marker.type === type && Date.now() - marker.time < 300;
    }

    private getNativeClipboardShortcutType(event: ClipboardEvent): ClipboardShortcutType | null {
        if (event.type === "copy") {
            return "copy";
        }

        if (event.type === "paste") {
            return "paste";
        }

        return null;
    }

    private getHistoryShortcutType(event: KeyboardEvent): HistoryShortcutType | null {
        const hasCommandModifier = event.ctrlKey || event.metaKey;
        if (!hasCommandModifier || event.altKey) {
            return null;
        }

        if (event.code === "KeyZ") {
            return event.shiftKey ? "redo" : "undo";
        }

        if (event.code === "KeyY" && !event.shiftKey) {
            return "redo";
        }

        return null;
    }

    private isEditableShortcutTarget(event: Event) {
        return event.composedPath().some((target) => {
            if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
                return true;
            }

            if (target instanceof HTMLSelectElement) {
                return true;
            }

            return target instanceof HTMLElement && target.isContentEditable;
        });
    }

    private shouldSkipBoardShortcut(event: Event) {
        return (
            event.defaultPrevented ||
            this.isEditableShortcutTarget(event) ||
            canv.appInterface.inputFocused !== 0 ||
            canv.textEditingController.getIsEditing() ||
            this.isActiveOrNewVoteMode() ||
            canv.toolBox.currentTool?.name === "voteTool" ||
            canv.toolBox.currentTool?.name === "activeVoteScreen" ||
            canv.toolBox.currentTool?.name === "pngExportTool"
        );
    }

    private shouldSkipNativeClipboardShortcut(
        event: ClipboardEvent,
        type: ClipboardShortcutType,
    ): boolean {
        const isBoardView = canv.isBoardModeView();
        const isNotCursorTool = canv.toolBox.currentTool?.name !== "cursorTool";
        const nothingSelected = canv.canvasController.selected.length === 0;

        return (
            this.wasShortcutRecentlyHandled(this.lastNativeClipboardShortcut, type) ||
            this.shouldSkipBoardShortcut(event) ||
            isNotCursorTool ||
            (type === "copy" && isBoardView && !canv.isBoardModeViewCopy()) ||
            (type === "paste" && isBoardView) ||
            (type === "copy" && nothingSelected)
        );
    }

    private shouldPasteIntoSelectedTextWidget(event: ClipboardEvent): boolean {
        if (!isWidgetWithTextOrTextWidgetOnly()) {
            return false;
        }

        const eventData = getClipboardEventData(event.clipboardData);

        return (
            !!eventData &&
            eventData.images.length === 0 &&
            !isJespoDumpsText(eventData.text) &&
            (!!eventData.html || !!eventData.text)
        );
    }

    nativeClipboardShortcutListener = (event: ClipboardEvent): void => {
        const shortcutType = this.getNativeClipboardShortcutType(event);
        if (!shortcutType || this.shouldSkipNativeClipboardShortcut(event, shortcutType)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.markNativeClipboardShortcutHandled(shortcutType);

        if (shortcutType === "copy") {
            canv.canvasController.copyProccessor().catch(logException);
            return;
        }

        if (this.shouldPasteIntoSelectedTextWidget(event)) {
            canv.toolBox
                .TryGetToolByName("cursorTool")
                ?.TryInteractionOnly(canv.canvasController.selected[0], "pastText");
            return;
        }

        pasteProccessor(event).catch(logException);
    };

    historyShortcutFallbackListener = (event: KeyboardEvent): void => {
        const shortcutType = this.getHistoryShortcutType(event);
        if (!shortcutType || this.shouldSkipBoardShortcut(event) || canv.isBoardModeView()) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (shortcutType === "undo") {
            canv.eventManager.Undo();
            return;
        }

        canv.eventManager.Redo();
    };

    // --- Pointer/Mouse Events ---

    dropElement = (e: DragEvent): void => {
        e.preventDefault();
        if (canv.appInterface.activeVote.isOpen || this.isActiveOrNewVoteMode()) return;

        const files = e.dataTransfer?.files;
        if (files && files.length > 0) {
            loadFiles(files, { x: e.pageX, y: e.pageY });
        }
    };

    pointerDownListener = (evt: PointerEvent): void => {
        evt.preventDefault();
        // Очистка стейта при потере фокуса и не срабатывании handleKeyboardUp, например переход в создание скриншота на MacOS
        // Если ни один модификатор физически не нажат и стейт не пуст — очищаем наш стейт
        if (!evt.metaKey && !evt.shiftKey && !evt.altKey && !evt.ctrlKey) {
            if (canv.keyboard.command) canv.keyboard.command = "";
            if (canv.keyboard.code) canv.keyboard.code = "";
        }

        if (evt.pointerType === "mouse") {
            try {
                canv.actionManager.startClickMove();
                const isRightButtonClicked = evt.which === 3 || evt.button === 2;
                canv.contextMenu.hide();

                canv.actionManager.lastX = evt.offsetX || evt.pageX - canv.canvas.offsetLeft;
                canv.actionManager.lastY = evt.offsetY || evt.pageY - canv.canvas.offsetTop;

                if (isRightButtonClicked) {
                    canv.actionManager.rightButtonClicked = true;
                    return;
                }

                canv.toolBox.TryAction("mousedown");
                canv.actionManager.preX = canv.actionManager.lastX;
                canv.actionManager.preY = canv.actionManager.lastY;
                canv.redraw();
            } catch (e) {
                logException(e);
            }
        }
    };

    pointerMoveListener(evt: PointerEvent): void {
        if (isTextEditorPointerEvent(canv, evt)) {
            return;
        }

        evt.preventDefault();

        // когда мы зажимаем ПКМ и заводим за границу канваса
        // и там отпускаем ПКМ при возвращении на канвас мы
        // должны снять rightButtonClicked что бы не вертеть доску
        if (canv.actionManager.rightButtonClicked && evt.buttons !== 2) {
            canv.actionManager.rightButtonClicked = false;
            return;
        }

        if (evt.pointerType === "mouse") {
            try {
                canv.actionManager.stepClickMove();
                canv.actionManager.lastX = evt.clientX || evt.pageX - canv.canvas.offsetLeft;
                canv.actionManager.lastY = evt.clientY || evt.pageY - canv.canvas.offsetTop;
                canv.actionManager.lastMoveEvt = evt;

                if (canv.actionManager.mode === "mouse" && canv.actionManager.rightButtonClicked) {
                    canv.multiManager.resetViewSubscribe();
                    matrixTranslate(evt.movementX * canv.dpr, evt.movementY * canv.dpr);
                    canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
                    canv.toolBox.TryAction("touchpadGestureDragging");
                    canv.redraw();

                    if (canv.getInitDataProp("navigationMode") === "" && !isMobile()) {
                        canv.appInterface.navigationSelectWindow.open();
                    }
                    return;
                }

                canv.toolBox.TryAction("mousemove");
                canv.actionManager.preX = canv.actionManager.lastX;
                canv.actionManager.preY = canv.actionManager.lastY;
                canv.multiManager.enableCursorUpdate();
            } catch (e) {
                logException(e);
            }
        }
    }

    pointerUpListener(evt: PointerEvent): void {
        if (isTextEditorPointerEvent(canv, evt)) {
            return;
        }

        evt.preventDefault();
        this.prevTouches = [];
        this.gestureType = undefined;

        try {
            const isRightButtonClicked = evt.which === 3 || evt.button === 2;
            if (canv.appInterface.activeVote.isOpen && isRightButtonClicked) return;

            // используется только для майндмапой, чтобы смотреть клик был правой кнопкой или нет
            // пользоваться полем rightButtonClicked не получается, так как она меняется назад на false до срабатывания события mouseup
            canv.actionManager.rightButtonClickedPrevValue = canv.actionManager.rightButtonClicked;

            if (isRightButtonClicked) {
                canv.actionManager.rightButtonClicked = false;
                if (canv.actionManager.isClick()) {
                    if (canv.toolBox.currentTool !== canv.toolBox.getFirstTool()) {
                        canv.toolBox.switchTo(canv.toolBox.getFirstTool());
                    }
                    if (!selectionManager.isOverSelected()) {
                        canv.toolBox.TryAction("mousedown");
                        canv.toolBox.TryAction("mouseup");
                    }
                    canv.contextMenu.show();
                    canv.redraw();
                    return;
                }
            }

            const targetIsCanvas = evt.target === canv.canvas;
            // mouseup на документе должно срабатывать только если
            // мы что то переноси, а не кликаем. Таким образом мы можем
            // растянуть элемент за канвас, нарисовать линию за канвас
            // но при клике на боковое меню или за пределами канваса
            // мы не можем создавать элементы
            if (canv.actionManager.clickMove > 1 || targetIsCanvas) {
                canv.toolBox.TryAction("mouseup");
                canv.redraw();
            }
        } catch (e) {
            logException(e);
        }

        canv.actionManager.stopClickMove();
    }

    // --- Touch Events ---

    touchStartListener = (evt: TouchEvent): void => {
        evt.preventDefault();
        this.gestureType = undefined;
        this.prevTouches = [];
        const touches = [...evt.touches] as TouchWithOffset[];
        this.lastTouches = touches;

        if (touches.length > 2) return;

        canv.actionManager.startClickMove();
        canv.contextMenu.hide();

        if (touches.length === 1) {
            // стандартное поведение, как для мышки
            const touch = touches[0];
            canv.actionManager.lastX = touch.offsetX || touch.pageX - canv.canvas.offsetLeft;
            canv.actionManager.lastY = touch.offsetY || touch.pageY - canv.canvas.offsetTop;
        } else if (touches.length === 2) {
            // 2 пальца
            const { x, y } = touches
                .map((touch) => ({
                    x: touch.offsetX || touch.pageX - canv.canvas.offsetLeft,
                    y: touch.offsetY || touch.pageY - canv.canvas.offsetTop,
                }))
                .reduce(
                    ({ x: x1, y: y1 }, { x: x2, y: y2 }) => ({
                        x: (x1 + x2) / 2,
                        y: (y1 + y2) / 2,
                    }),
                    { x: 0, y: 0 },
                );
            canv.actionManager.lastX = x;
            canv.actionManager.lastY = y;
        }

        canv.toolBox.TryAction("mousedown");
        canv.actionManager.preX = canv.actionManager.lastX;
        canv.actionManager.preY = canv.actionManager.lastY;
        this.prevTouches = this.lastTouches;
        canv.redraw();
    };

    touchMoveListener = (evt: TouchEvent): void => {
        evt.preventDefault();
        const touches = [...evt.touches] as TouchWithOffset[];
        if (touches.length > 2) return;

        this.lastTouches = touches;
        canv.actionManager.stepClickMove();

        if (touches.length === 1) {
            // стандартное поведение, как для мышки
            const touch = touches[0];
            canv.actionManager.lastX = touch.offsetX || touch.pageX - canv.canvas.offsetLeft;
            canv.actionManager.lastY = touch.offsetY || touch.pageY - canv.canvas.offsetTop;
            // @ts-ignore
            canv.actionManager.lastMoveEvt = evt;
            canv.toolBox.TryAction("mousemove");
            canv.actionManager.preX = canv.actionManager.lastX;
            canv.actionManager.preY = canv.actionManager.lastY;
        } else if (touches.length === 2 && this.prevTouches.length === 2) {
            // 2 пальца
            // если тип жеста не определен - определяем
            if (this.gestureType === undefined) {
                // вычисляем разницу в движении пальцев
                const distanceStart = Math.hypot(
                    this.prevTouches[0].clientX - this.prevTouches[1].clientX,
                    this.prevTouches[0].clientY - this.prevTouches[1].clientY,
                );
                const distanceNow = Math.hypot(
                    touches[0].clientX - touches[1].clientX,
                    touches[0].clientY - touches[1].clientY,
                );
                const distanceDelta = Math.abs(distanceStart - distanceNow);
                const moveFirst = Math.hypot(
                    this.prevTouches[0].clientX - touches[0].clientX,
                    this.prevTouches[0].clientY - touches[0].clientY,
                );
                const moveSecond = Math.hypot(
                    this.prevTouches[1].clientX - touches[1].clientX,
                    this.prevTouches[1].clientY - touches[1].clientY,
                );
                const moveDelta = (moveFirst + moveSecond) / 2;

                if (distanceDelta > moveDelta) {
                    // пользователь раздвигает/сводит пальцы
                    this.gestureType = "pinch";
                    // сохраняем точку посередине, чтобы зумить по ней
                    canv.actionManager.lastX =
                        (touches[0].clientX + touches[1].clientX) / 2 - canv.canvas.offsetLeft;
                    canv.actionManager.lastY =
                        (touches[0].clientY + touches[1].clientY) / 2 - canv.canvas.offsetTop;
                } else {
                    // двигает пальцы вместе
                    this.gestureType = "pan";
                }
            }

            if (this.gestureType === "pan") {
                // перемещение
                const dx =
                    (touches[0].clientX -
                        this.prevTouches[0].clientX +
                        touches[1].clientX -
                        this.prevTouches[1].clientX) /
                    2;
                const dy =
                    (touches[0].clientY -
                        this.prevTouches[0].clientY +
                        touches[1].clientY -
                        this.prevTouches[1].clientY) /
                    2;
                canv.actionManager.dragAction(dx * canv.dpr, dy * canv.dpr);
            } else if (this.gestureType === "pinch") {
                // масштабирование
                const distanceStart = Math.hypot(
                    this.prevTouches[0].clientX - this.prevTouches[1].clientX,
                    this.prevTouches[0].clientY - this.prevTouches[1].clientY,
                );
                const distanceNow = Math.hypot(
                    touches[0].clientX - touches[1].clientX,
                    touches[0].clientY - touches[1].clientY,
                );
                const distanceDelta = distanceNow - distanceStart;
                const actionPoint = canv.actionManager.getActionPoint();
                canv.actionManager.handleZoomFor2(distanceDelta * -1, {
                    x: actionPoint.x * canv.m[0],
                    y: actionPoint.y * canv.m[0],
                });
            }
        }

        this.prevTouches = this.lastTouches;
        canv.redraw();
    };

    dblClickListener(evt: MouseEvent): void {
        try {
            canv.toolBox.TryAction("dblclick");
            canv.redraw();
        } catch (e) {
            logException(e);
        }
    }

    clickListener = (evt: MouseEvent): void => {
        if (window.self !== window.top) window.focus();
        if (evt.detail === 3) canv.toolBox.TryAction("tripleclick");
    };

    // --- Keyboard ---

    isActiveOrNewVoteMode = (): boolean => {
        return (
            canv.appInterface.activeVote.isOpen ||
            (canv.appInterface.voteWindow.opened &&
                canv.appInterface.voteWindow.activeTab === "new")
        );
    };

    handleKeyboardDown(e: KeyboardEvent, force = false): void {
        if (canv.isBoardModeView() && !force) return;
        if (canv.toolBox.currentTool?.name === "voteTool") return;
        if (canv.toolBox.currentTool?.name === "activeVoteScreen") return;
        if (canv.toolBox.currentTool?.name === "imageExportTool") {
            if (canv.keyboard.isEscCommand()) {
                canv.appInterface.imageExportWindow.close();
            }
            return;
        }

        try {
            const isSelectedOnlyOneTextWidget = isWidgetWithTextOrTextWidgetOnly();
            const isInteractionMode = canv.toolBox
                .TryGetToolByName("cursorTool")
                .isMode("interaction");

            // Нужно, например, чтобы когда вводятся символы в input of widgetMenu размера текста, они не писались в стикер
            // inputFocused == 2 нужен для Enter. Без этого (else if) если нажать в input enter, то enter обработается (поставиться в стикер и т.д.)
            // Общее правило - для всех input надо ловить focus, blur и проставлять canv.appInterface.inputFocused
            if (canv.appInterface.inputFocused === 1 || this.isActiveOrNewVoteMode()) return;
            if (canv.appInterface.inputFocused === 2) {
                canv.appInterface.inputFocused = 0;
                return;
            }

            if (canv.keyboard.isUndoCommand()) {
                e.preventDefault();
                e.stopPropagation();
                canv.eventManager.Undo();
                return;
            }

            const arrowActions: Array<[() => boolean, 1 | -1, "x" | "y"]> = [
                [() => canv.keyboard.isLeftArrow(), 1, "x"],
                [() => canv.keyboard.isRightArrow(), -1, "x"],
                [() => canv.keyboard.isUpArrow(), 1, "y"],
                [() => canv.keyboard.isDownArrow(), -1, "y"],
            ];

            for (const [check, sign, axis] of arrowActions) {
                if (!isInteractionMode && check()) {
                    const props: ZoomToVisTransformation = {
                        0: canv.m[0],
                        3: canv.m[3],
                        4:
                            axis === "x"
                                ? canv.m[4] + sign * canv.actionManager.getConstant(50)
                                : canv.m[4],
                        5:
                            axis === "y"
                                ? canv.m[5] + sign * canv.actionManager.getConstant(50)
                                : canv.m[5],
                    };
                    canv.actionManager.softDragAction(props);
                    return;
                }
            }

            if (!isInteractionMode && canv.keyboard.isEqual()) {
                canv.actionManager.zoomPlus();
                return;
            }
            if (!isInteractionMode && canv.keyboard.isDash()) {
                canv.actionManager.zoomMinus();
                return;
            }
            if (canv.keyboard.isZoomPlus()) {
                canv.actionManager.zoomPlus();
                e.preventDefault();
                return;
            }
            if (canv.keyboard.isZoomMinus()) {
                canv.actionManager.zoomMinus();
                e.preventDefault();
                return;
            }
            if (canv.keyboard.isRedoCommand()) {
                canv.eventManager.Redo();
                return;
            }

            if (!isInteractionMode && canv.keyboard.isEscCommand()) {
                if (
                    canv.appInterface.voteWindow.opened &&
                    canv.appInterface.voteWindow.activeTab === "new"
                ) {
                    canv.toolBox.switchToByName("voteTool");
                } else if (canv.appInterface.activeVote.isOpen) {
                    canv.toolBox.switchToByName("activeVoteTool");
                } else {
                    const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");
                    // @ts-ignore
                    if (cursorTool?.fastActionGroup) {
                        // Делаем beforeDeletion потому-что группы как таковой нет в canv.canvasController
                        // Она просто локально существует в cursorTool
                        // @ts-ignore
                        cursorTool.fastActionGroup.beforeDeletion();
                        // @ts-ignore
                        cursorTool.setSelectedAndInteraction(cursorTool.fastActionGroup);
                    }
                    canv.toolBox.switchToByName("cursorTool");
                }
            }

            // todo test it more
            const toolSwitches: Array<[() => boolean, string, (() => void)?]> = [
                [
                    () => canv.keyboard.isPenToolCommand(),
                    "penTool",
                    () => {
                        const penTool = canv.toolBox.TryGetToolByName("penTool");
                        const subMenu = document.getElementsByClassName("penTool_subMenu")[0];
                        const isMenuOpenWithOtherTool = penTool.mode !== "pen" && subMenu;
                        canv.toolBox.switchTo(penTool, !isMenuOpenWithOtherTool);
                        penTool.setMode("pen");
                    },
                ],
                [() => canv.keyboard.isCardToolCommand(), "cardTool"],
                [() => canv.keyboard.isShapeToolCommand(), "shapeTool"],
                [
                    () => canv.keyboard.isShapeToolCommandWithCircleMode(),
                    "",
                    () => canv.toolBox.switchToShapeTool("circle"),
                ],
                [
                    () => canv.keyboard.isShapeToolCommandWithRectMode(),
                    "",
                    () => canv.toolBox.switchToShapeTool("rect"),
                ],
                [() => canv.keyboard.isStickerToolCommand(), "stickerTool"],
                [() => canv.keyboard.isTextToolCommand(), "textTool"],
                [
                    () =>
                        canv.keyboard.isCommentToolCommand() &&
                        !canv.isGuest() &&
                        canv.getInitDataProp("isProfessional"),
                    "commentTool",
                ],
                [() => canv.keyboard.isArrowToolCommand(), "arrowTool"],
                [
                    () => canv.keyboard.isEraserToolCommand(),
                    "penTool",
                    () => {
                        const penTool = canv.toolBox.TryGetToolByName("penTool");
                        const subMenu = document.getElementsByClassName("penTool_subMenu")[0];
                        const isMenuOpenWithOtherTool = penTool.mode !== "eraser" && subMenu;
                        canv.toolBox.switchTo(penTool, !isMenuOpenWithOtherTool);
                        penTool.setMode("eraser");
                    },
                ],
            ];

            for (const [check, toolName, action] of toolSwitches) {
                if (!isInteractionMode && !isSelectedOnlyOneTextWidget && check()) {
                    if (action) action();
                    else {
                        canv.toolBox.switchTo(canv.toolBox.TryGetToolByName(toolName as ToolName));
                    }
                }
            }

            if (
                !isInteractionMode &&
                !isSelectedOnlyOneTextWidget &&
                canv.keyboard.isMouseOrHandCommand()
            ) {
                const dragTool = canv.toolBox.TryGetToolByName("dragTool");
                const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");
                canv.toolBox.switchTo(dragTool.isSelected ? cursorTool : dragTool);
            }

            if (!isInteractionMode && canv.keyboard.isSearchShortcut()) {
                // нужен, иначе выводится и окно поиска в браузере
                e.preventDefault();
                return;
            }
            // функционал должен быть доступен и при редактировании текста, то есть в isInteractionMode
            if (canv.keyboard.isBoldShortcut()) {
                e.preventDefault();
                return;
            }
            if (!isInteractionMode && canv.keyboard.isZoomToSelected())
                canv.zoomToSelected(canv.canvasController.selected);
            if (!isInteractionMode && canv.keyboard.isZoomToFit())
                canv.zoomToSelected(canv.canvasController.items);
            if (!isInteractionMode && canv.keyboard.isZoomTo100()) canv.zoomTo100();

            canv.toolBox.TryAction("keyboardDown");
            canv.redraw();
            // Для выделенного текстового виджета первый символ вставляется вручную в textV2.isInteraction("insertText").
            // Здесь глушим нативный ввод браузера, чтобы не получить дубль символа в Quill.
            const shouldPreventDefault =
                !canv.appInterface.inputFocused ||
                (canv.keyboard.isSymbol && isSelectedOnlyOneTextWidget);
            if (shouldPreventDefault) {
                // нужен, иначе cmd + [ кроме bring to front еще переключает страницу браузера на предыдущую
                e.preventDefault();
            }
        } catch (e) {
            logException(e);
        }
    }

    handleKeyboardUp(event: KeyboardEvent): void {
        canv.keyboard.command = "";
        canv.keyboard.code = "";

        const isAltKey =
            event?.code === "AltLeft" || event?.code === "AltRight" || event?.key === "Alt";
        if (!isAltKey || !canv.actionManager.isMouseDown()) return;

        const cursor = canv.toolBox.TryGetToolByName("cursorTool");
        if (cursor?.isMode?.("copying")) {
            switchCopyingToDragging(canv, cursor);
        }
    }

    // --- Click Move ---

    startClickMove(): void {
        canv.actionManager.clickMove = 1;
    }
    stopClickMove(): void {
        canv.actionManager.clickMove = 0;
    }

    stepClickMove(): void {
        if (canv.actionManager.clickMove > 0 && canv.actionManager.clickMove < 30) {
            // < 30 это защита, если вдруг clickMove будет бесконечно рости
            canv.actionManager.clickMove++;
        }
    }

    getClickMoveValue(): number {
        return canv.actionManager.clickMove;
    }

    isClick(): boolean {
        // return canv.actionManager.clickMove === 1;
        // тут решается проблема двух корнер кейсов -
        // 1 wm_filter_submenu меню когда выбрал стикер и фигуру и потом фильтруешь фигуру,
        // 2 кликодраги - это создавало впечателение что клик не сработал, на самом деле это был не клик а частично драг - 2 у него было клик мув
        // сечас это условие ниже почему-то работает для обоих кейсов. с < 3  работал только для кликов, меню лагало.
        return this.clickMove > 0 && this.clickMove <= this.clickMoveThreshold;
    }

    isMouseDown(): boolean {
        return canv.actionManager.clickMove > 0;
    }

    // --- Mode ---

    setTrackpadMode(): void {
        this.mode = "trackpad";
        setNavigationModeLS("trackpad");
    }

    setMode(mode: NavigationMode): void {
        this.mode = mode;
        canv.setInitDataProp("navigationMode", mode);
        setNavigationModeLS(mode);
        if (canv.isGrantedAccess()) return;
        if (!canv.getInitDataProp("temporary")) {
            MakeRequest(
                ACCOUNT_UPDATE_V2,
                { navigationMode: mode, token: canv.getInitDataProp("userToken") },
                true,
            );
        }
    }

    // --- Zoom & Pan ---

    handleTouchpadGesture(evt: WheelEvent): void {
        canv.multiManager.resetViewSubscribe();
        canv.appInterface.linkPopupController.onViewportTransformChange();
        // При прокуртки колесика мышки вызывается else (touchpadGestureDragging) (!evt.ctrlKey)
        canv.multiManager.enableCursorUpdate();
        canv.contextMenu.hide();

        if (canv.getInitDataProp("navigationMode") === "" && !isMobile()) {
            canv.appInterface.navigationSelectWindow.open();
        }

        if (evt.ctrlKey || canv.actionManager.mode === "mouse") {
            canv.actionManager.handleZoomingExp2(evt);
            canv.toolBox.TryAction("touchpadGestureZoom");
        } else {
            canv.actionManager.dragAction(-evt.deltaX * canv.dpr, -evt.deltaY * canv.dpr);
        }
    }

    handleDocumentTouchpadGesture(e: WheelEvent): false | void {
        let shouldDo = false;
        const path = e.composedPath();

        // todo: need test to it!
        for (const el of path) {
            const element = el as HTMLElement;
            if (!element.classList) continue;
            const isTextEditorOverlay = !!element.closest(".editor-box");
            const iff =
                isTextEditorOverlay ||
                ((element.classList.contains("widgetMenu") ||
                    element.classList.contains("widgetSubMenu") ||
                    element.classList.contains("dragThrowAble")) &&
                    !element.classList.contains("scrollable"));
            if (iff) {
                shouldDo = true;
                break;
            }
        }

        if (shouldDo) canv.actionManager.handleTouchpadGesture(e);

        for (const el of path) {
            const element = el as HTMLElement;
            if (element.classList?.contains("scrollable")) return;
        }

        e.preventDefault();
        return false;
    }

    handleZoomingExp2(evt: WheelEvent): false {
        const pt = canv.actionManager.getActionPoint();
        canv.actionManager.handleZoomFor2(evt.deltaY, { x: pt.x * canv.m[0], y: pt.y * canv.m[0] });
        evt.preventDefault();
        return false;
    }

    handleZoomFor2(deltaY: number, pt: Point): void {
        let zoomAlpha = 0.01 * -deltaY;
        // Для зума колесиком уменьшаем зум на каждый скролл
        if (canv.actionManager.mode === "mouse") zoomAlpha *= 0.1;

        const maxZ = this.getConstant(this.maxZoom);
        const minZ = this.getConstant(this.minZoom);

        if (zoomAlpha > 0) {
            if (
                canv.m[0] + zoomAlpha * canv.m[0] > maxZ ||
                canv.m[3] + zoomAlpha * canv.m[3] > maxZ
            ) {
                const customZoomAlpha = maxZ / canv.m[0] - 1;
                canv.m[0] = maxZ;
                canv.m[3] = maxZ;
                canv.m[4] -= pt.x * customZoomAlpha;
                canv.m[5] -= pt.y * customZoomAlpha;
            } else {
                canv.m[0] += zoomAlpha * canv.m[0];
                canv.m[3] += zoomAlpha * canv.m[3];
                canv.m[4] -= pt.x * zoomAlpha;
                canv.m[5] -= pt.y * zoomAlpha;
            }
        } else {
            if (
                canv.m[0] + zoomAlpha * canv.m[0] < minZ ||
                canv.m[3] + zoomAlpha * canv.m[3] < minZ
            ) {
                const customZoomAlpha = 1 - minZ / canv.m[0];
                canv.m[0] = minZ;
                canv.m[3] = minZ;
                canv.m[4] += pt.x * customZoomAlpha;
                canv.m[5] += pt.y * customZoomAlpha;
            } else {
                canv.m[0] += zoomAlpha * canv.m[0];
                canv.m[3] += zoomAlpha * canv.m[3];
                canv.m[4] -= pt.x * zoomAlpha;
                canv.m[5] -= pt.y * zoomAlpha;
            }
        }

        // Обновляем отображаемый процент зума
        canv.updateZoomDisplay();
        canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
        canv.redraw();
    }

    // --- Minimap ---

    updateMinimap = rafThrottle(() => {
        eventEmitter.dispatch("minimap.update", { needUpdate: true });
    });

    // --- Helpers ---

    softDragAction(zoomToVisProps: ZoomToVisTransformation): void {
        const animation = new ZoomToVis(zoomToVisProps, 15);
        canv.animator.deleteAnimation("zoomTo");
        canv.animator.addAnimation(animation);
        canv.redraw();
    }

    dragAction(x: number, y: number): void {
        matrixTranslate(x, y);
        canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
        this.updateMinimap();
        canv.toolBox.TryAction("touchpadGestureDragging");
        canv.redraw();
    }

    async viewSaveSave(): Promise<void> {
        if (!canv.isBoardModeFullRights()) return;
        const viewPort = getViewport();
        const request: ZoomSaveRequest = {
            token: canv.getInitDataProp("userToken"),
            boardUID: canv.getInitDataProp("boardUID"),
            data: {
                viewportStartX: viewPort.viewportStartX,
                viewportStartY: viewPort.viewportStartY,
                viewportWidth: viewPort.viewportWidth,
                viewportHeight: viewPort.viewportHeight,
                scale: canv.m[0],
            },
        };
        await MakeRequest(BOARD_ZOOM_SET_V2, request, true, true, true);
    }

    async viewSaveLoad(): Promise<void> {
        if (!canv.isBoardModeFullRights()) return;

        const zoom: ZoomResponse | undefined = await MakeRequest(
            BOARD_ZOOM_GET_V2,
            {
                token: canv.getInitDataProp("userToken"),
                boardUID: canv.getInitDataProp("boardUID"),
            },
            false,
            true,
        );

        // ручка niceToHave - может вернуть undefined
        // был кейс что пришел success с нулями. В таком случае мы не хотим туда зумить
        if (!zoom || zoom.status === "fail" || zoom.data.scale === 0) return;

        const centerX = zoom.data.viewportStartX + zoom.data.viewportWidth / 2;
        const centerY = zoom.data.viewportStartY + zoom.data.viewportHeight / 2;
        const maxScale = Math.max(zoom.data.scale, this.getConstant(this.minZoom));

        canv.m[0] = maxScale;
        canv.m[3] = maxScale;
        canv.m[4] = -(centerX - zoom.data.viewportWidth / 2) * canv.m[0];
        canv.m[5] = -(centerY - zoom.data.viewportHeight / 2) * canv.m[3];

        canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
        canv.updateZoomDisplay();
    }

    hasItemInViewport(): boolean {
        const cv = getViewport();
        const iv = canv.canvasController.getSelectedProps(canv.canvasController.items);
        return (
            cv.viewportStartX + cv.viewportWidth >= iv.startX &&
            cv.viewportStartY + cv.viewportHeight >= iv.startY &&
            cv.viewportStartX <= iv.startX + iv.width &&
            cv.viewportStartY <= iv.startY + iv.height
        );
    }

    zoomPlus(): void {
        canv.actionManager.handleZoomFor2(-20, {
            x: -canv.m[4] + canv.canvas.width / 2,
            y: -canv.m[5] + canv.canvas.height / 2,
        });
    }

    zoomMinus(): void {
        canv.actionManager.handleZoomFor2(20, {
            x: -canv.m[4] + canv.canvas.width / 2,
            y: -canv.m[5] + canv.canvas.height / 2,
        });
    }
}
